import { Link } from '@tanstack/react-router';
import { Play, Square } from 'lucide-react';
import { useRef, useState } from 'react';

import { isOutboundConversationCall } from '@apps/web/components/shared/messages/conversation_call_utils';
import { normalizeCallTranscriptText } from '@apps/web/components/voice_calls/call_transcript_display_utils';
import { formatVoiceCallOutreachLinkLabel } from '@apps/web/components/voice_calls/voice_call_outreach_utils';
import {
    type CallIconTone,
    getVoiceCallTranscriptPresentation,
} from '@apps/web/components/voice_calls/voice_call_transcript_card';
import {
    playMediaElement,
    RECORDING_PLAYBACK_ERROR_MESSAGE,
} from '@apps/web/lib/browser_audio';
import { isS3RecordingUrl } from '@apps/web/lib/contact_utils';
import { formatTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type ConversationCall = NonNullable<
    ApiResponseType<
        '/organizations/{organization_id}/conversations/{conversation_id}',
        'get'
    >['calls']
>[number];

// Tinted (not solid) tones for the small status icon so the compact chat
// card stays visually quiet next to message bubbles.
const compactIconToneClassNames = {
    danger: 'bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400',
    info: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
    success: 'bg-muted text-muted-foreground',
    muted: 'bg-muted text-muted-foreground',
} satisfies Record<CallIconTone, string>;

/**
 * A voice call rendered as a chat bubble in the conversation timeline.
 *
 * Calls sit in the thread like messages: Phoebe-initiated (outbound) calls
 * align right with sent messages, caller-initiated (inbound) calls align
 * left with received messages. The card is always white regardless of
 * direction. When a playable recording exists the leading icon is a play
 * button that plays the recording inline; clicking anywhere else on the
 * card opens the call detail dialog.
 */
export function CallTimelineItem({
    call,
    timezone,
    onOpen,
    prefetchedRecordingUrl,
}: {
    readonly call: ConversationCall;
    readonly timezone: string;
    readonly onOpen: () => void;
    readonly prefetchedRecordingUrl?: string;
}) {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackError, setPlaybackError] = useState<string | null>(null);
    const isOutboundCall = isOutboundConversationCall(call);
    const timestamp = call.started_at ?? call.created_at;
    const presentation = getVoiceCallTranscriptPresentation(call);
    const summaryText =
        normalizeCallTranscriptText(call.call_summary) ??
        (presentation.title === 'Transferred call'
            ? presentation.subtitle
            : null);
    const outreachId = isOutboundCall ? (call.callout_id ?? null) : null;
    const outreachLinkLabel = outreachId
        ? formatVoiceCallOutreachLinkLabel({
              outreachType: call.outreach_type,
              outreachClientNames: call.outreach_client_names,
              earliestShiftStartTime: call.outreach_earliest_shift_start_time,
              shiftCount: call.outreach_shift_count,
              timezone,
          })
        : null;
    // S3 recordings need a signed URL (prefetched by the conversation);
    // legacy Retell URLs are public and playable directly.
    const playableRecordingUrl =
        prefetchedRecordingUrl ??
        (call.recording_url && !isS3RecordingUrl(call.recording_url)
            ? call.recording_url
            : null);
    const StatusIcon = presentation.Icon;

    function toggleRecordingPlayback() {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        if (audio.paused) {
            setPlaybackError(null);
            playMediaElement(audio).catch(() =>
                setPlaybackError(RECORDING_PLAYBACK_ERROR_MESSAGE),
            );
        } else {
            // Stop, not pause: the next play starts from the beginning.
            audio.pause();
            audio.currentTime = 0;
        }
    }

    return (
        <div
            className={cn(
                'group/message flex w-full',
                isOutboundCall ? 'justify-end' : 'justify-start',
            )}
            data-testid="call-timeline-row"
        >
            <div className="relative max-w-[24rem]">
                <div className="bg-card border-border hover:bg-accent/40 relative rounded-2xl border p-2 pr-2.5 shadow-sm transition-colors">
                    <button
                        aria-label="Open call details"
                        className="focus-visible:ring-ring absolute inset-0 rounded-2xl focus-visible:ring-2 focus-visible:outline-none"
                        onClick={onOpen}
                        type="button"
                    />
                    <div className="pointer-events-none relative flex items-center gap-2.5">
                        {playableRecordingUrl ? (
                            <button
                                aria-label={
                                    isPlaying
                                        ? 'Stop call recording'
                                        : 'Play call recording'
                                }
                                className="bg-foreground text-background pointer-events-auto flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                                onClick={toggleRecordingPlayback}
                                type="button"
                            >
                                {isPlaying ? (
                                    <Square className="h-1.5 w-1.5 fill-current" />
                                ) : (
                                    <Play className="ml-px h-2 w-2 fill-current" />
                                )}
                            </button>
                        ) : (
                            <div
                                className={cn(
                                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                                    compactIconToneClassNames[
                                        presentation.tone
                                    ],
                                )}
                            >
                                <StatusIcon className="h-2.5 w-2.5" />
                            </div>
                        )}
                        <span className="text-foreground text-sm font-semibold whitespace-nowrap">
                            Call
                        </span>
                    </div>
                    {playbackError ? (
                        <p
                            className="text-muted-foreground pointer-events-none relative mt-1 ml-[1.625rem] pr-1 text-xs leading-5"
                            role="status"
                        >
                            {playbackError}
                        </p>
                    ) : null}
                    {summaryText ? (
                        <p className="text-muted-foreground pointer-events-none relative mt-1 ml-[1.625rem] line-clamp-2 pr-1 text-xs leading-5">
                            {summaryText}
                        </p>
                    ) : null}
                    {outreachId && outreachLinkLabel ? (
                        <p className="relative mt-1 ml-[1.625rem] pr-1 text-xs">
                            <Link
                                className="text-foreground pointer-events-auto font-medium underline-offset-2 hover:underline"
                                params={{ 'callout-id': outreachId }}
                                to="/outreaches/$callout-id"
                            >
                                {outreachLinkLabel}
                            </Link>
                        </p>
                    ) : null}
                    {playableRecordingUrl ? (
                        <audio
                            className="hidden"
                            onEnded={() => setIsPlaying(false)}
                            onPause={() => setIsPlaying(false)}
                            onPlay={() => setIsPlaying(true)}
                            preload="none"
                            ref={audioRef}
                            src={playableRecordingUrl}
                        />
                    ) : null}
                </div>
                <span
                    className={cn(
                        'text-muted-foreground pointer-events-none absolute top-1/2 -translate-y-1/2 text-[11px] whitespace-nowrap opacity-0 transition-opacity group-hover/message:opacity-100',
                        isOutboundCall ? 'right-full mr-2' : 'left-full ml-2',
                    )}
                >
                    {formatTime(timestamp, timezone)}
                </span>
            </div>
        </div>
    );
}
